import {
  Alert,
  Button,
  Dialog,
  FormStack,
  InputAdornment,
  MenuItem,
  SelectField,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  ViewIcon,
} from '@all-manager/design-system-v2';
import type { ChangeEvent } from 'react';
import type { CreateDocumentAction } from '../../types/inbox.types';
import SubTopicAutocomplete from '../../../topics/external-components/SubTopicAutocomplete/SubTopicAutocomplete';
import ProviderAutocomplete from '../../../providers/external-components/ProviderAutocomplete/ProviderAutocomplete';
import { useCreateDocumentFromInboxModal } from './useCreateDocumentFromInboxModal';

type Props = {
  open: boolean;
  onClose: () => void;
  action: CreateDocumentAction | null;
  spaceId: string;
  onSuccess: () => void;
};

const AMOUNT_INPUT_SLOT_PROPS = {
  input: {
    endAdornment: <InputAdornment position="end">€</InputAdornment>,
  },
};

export default function CreateDocumentFromInboxModal(props: Props) {
  const {
    title,
    setTitle,
    subTopicId,
    setSubTopicId,
    documentTypeId,
    setDocumentTypeId,
    providerId,
    setProviderId,
    amount,
    setAmount,
    isQuote,
    isSubmitting,
    documentTypes,
    handleConfirm,
    handlePreviewFile,
  } = useCreateDocumentFromInboxModal(props);

  if (!props.action) return null;

  function handleTitleChange(e: ChangeEvent<HTMLInputElement>) {
    setTitle(e.target.value);
  }

  function handleSubTopicChange(newSubTopicId: string | null) {
    setSubTopicId(newSubTopicId || '');
  }

  function handleDocumentTypeChange(e: SelectChangeEvent<string>) {
    setDocumentTypeId(e.target.value);
  }

  function handleProviderChange(newProviderId: string | null) {
    setProviderId(newProviderId || '');
  }

  function handleAmountChange(e: ChangeEvent<HTMLInputElement>) {
    setAmount(e.target.value);
  }

  const suggestedProviderName = props.action.suggestedProviderName;
  const submitLabel = isQuote ? 'Enregistrer le devis' : 'Enregistrer le document';

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      title={submitLabel}
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: submitLabel,
          onClick: handleConfirm,
          loading: isSubmitting,
          disabled: isSubmitting || !title.trim() || (isQuote && !subTopicId),
        },
        secondaryAction: {
          label: 'Annuler',
          onClick: props.onClose,
          disabled: isSubmitting,
        },
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="body2" noWrap title={props.action.filename}>
            Fichier joint : <strong>{props.action.filename}</strong>
          </Typography>
          <Button
            variant="text"
            color="default"
            size="small"
            startIcon={<ViewIcon />}
            onClick={handlePreviewFile}
          >
            Voir le fichier
          </Button>
        </Stack>

        {(isQuote || suggestedProviderName) && (
          <Alert severity="info">
            <Stack spacing={0.5}>
              {isQuote && (
                <span>
                  Ce document est identifié comme un <strong>Devis</strong>. Il sera enregistré dans les
                  documents et ajouté à l&apos;onglet Devis du sujet sélectionné.
                </span>
              )}
              {suggestedProviderName && (
                <span>
                  Prestataire détecté automatiquement : <strong>{suggestedProviderName}</strong>
                </span>
              )}
            </Stack>
          </Alert>
        )}

        <FormStack>
          <TextField
            label={isQuote ? 'Nom du devis' : 'Nom du document'}
            size="small"
            fullWidth
            required
            value={title}
            onChange={handleTitleChange}
          />

          <SubTopicAutocomplete
            label="Sujet associé"
            size="small"
            fullWidth
            required={isQuote}
            value={subTopicId || null}
            onChange={handleSubTopicChange}
          />

          {!isQuote && (
            <SelectField<string>
              label="Type de document"
              size="small"
              fullWidth
              value={documentTypeId}
              onChange={handleDocumentTypeChange}
            >
              <MenuItem value="">
                <em>Non spécifié</em>
              </MenuItem>
              {documentTypes.map((dt) => (
                <MenuItem key={dt.id} value={dt.id}>
                  {dt.name}
                </MenuItem>
              ))}
            </SelectField>
          )}

          <ProviderAutocomplete
            label="Prestataire"
            size="small"
            fullWidth
            value={providerId || null}
            onChange={handleProviderChange}
            defaultSearch={suggestedProviderName}
          />

          {isQuote && (
            <TextField
              label="Montant du devis (€ TTC)"
              size="small"
              fullWidth
              type="number"
              value={amount}
              onChange={handleAmountChange}
              slotProps={AMOUNT_INPUT_SLOT_PROPS}
            />
          )}
        </FormStack>
      </Stack>
    </Dialog>
  );
}
